import css from './index.module.scss';
import { calcEnergyGeneration, getLocalData, getAsynItem, SolarItemType, ProductItemType, RecommandMap, BungleImgMap, formatNumber } from './data';
import { cloneDeep } from 'lodash';
import { useLang } from '@/i18n';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import GetAppIcon from './Mapping';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Scrollbar, Navigation } from 'swiper';
import EcoImg from 'eco-design/base/eco-img';
import EcoLink from 'eco-design/base/eco-link';
import gtm from '@/utils/gtm';

interface F_GSProps {
    callBuy: (item: any, isLink?: boolean) => void;
    updateSolar: (item: any) => void;
}
export default function F_GS({ callBuy, updateSolar }: F_GSProps) {
    const { locale = '' } = useRouter();
    const { t } = useLang('vanliferoadtrips');
    const [gap, setGap] = useState<number>(20);
    const [open, setOpen] = useState<boolean>(false);
    const { solar_panels, products }: { solar_panels: SolarItemType[]; products: ProductItemType[] } = getLocalData(locale);
    const [solarPanels, setSolarPanels] = useState<SolarItemType[]>([]);
    const [productCards, setProductCards] = useState<ProductItemType[]>([]);
    const IS_ROW = ['au', 'za', 'kr'].includes(locale);
    const IS_UK = ['uk'].includes(locale);
    const IS_EU = ['eu', 'uk', 'de', 'fr', 'it', 'es', 'nl'].includes(locale);
    const suffix = IS_UK ? 'uk' : IS_EU ? 'eu' : IS_ROW ? locale : 'en';

    const handleChangeChild = (item: ProductItemType, solar: SolarItemType) => {
        const newProductCards = cloneDeep(productCards);
        const product = newProductCards?.find((p: ProductItemType) => p.variantId === item.variantId);
        if (product) {
            product?.children?.forEach((child: SolarItemType) => (child.isActive = child.variantId === solar.variantId));
        }
        setProductCards(newProductCards);
    };

    const handleQuantityChange = (item: ProductItemType, delta: number) => {
        const newProductCards = cloneDeep(productCards);
        const product = newProductCards?.find((p: ProductItemType) => p.variantId === item.variantId);
        if (product) {
            const child = product?.children?.find((c: SolarItemType) => c.isActive);
            if (child) {
                const { inventoryPolicy, inventoryQuantity } = child;
                const [min, max] = [0, inventoryPolicy === 'continue' ? 100 : Math.min(100, inventoryQuantity)];
                if (child.quantity + delta >= min && child.quantity + delta <= max) {
                    child.quantity = Math.max(child.quantity + delta, 0);
                }
            }
        }
        setProductCards(newProductCards);
    };

    const handleBuy = (card: ProductItemType, isLink?: boolean) => {
        const { productId, variantId, productTitle, skuTitle, variantImg, img, price, compareAtPrice, inventoryPolicy, inventoryQuantity, children } = card;
        if (inventoryPolicy !== 'continue' && inventoryQuantity <= 0) return;
        const title = productTitle || skuTitle || '';
        try {
            gtm.push({
                event: 'uaEvent',
                event_name: 'buy',
                product_name: title,
                position: `Outdoor Recreation Page_${title}`,
            });
            // fb埋点
            // @ts-ignore
            window?.fbq && window?.fbq('track', 'Search');
        } catch (e) {
            console.error(e);
        } finally {
            const [_, max] = [0, inventoryPolicy === 'continue' ? 100 : Math.min(100, inventoryQuantity)];
            const addCarts: any = [
                {
                    discountPrice: price,
                    originalPrice: compareAtPrice,
                    inventory: max,
                    productId,
                    variantId,
                    img: variantImg || img || '',
                    title,
                },
            ];
            const target = children?.find((item: any) => item?.isActive);
            if (target && target.quantity > 0) {
                const {
                    price,
                    compareAtPrice,
                    inventoryPolicy,
                    inventoryQuantity,
                    productId,
                    variantId,
                    variantImg,
                    img,
                    parentVariantId,
                    productTitle,
                    skuTitle,
                    quantity,
                } = target;
                const title = productTitle || skuTitle || '';
                const [_, max] = [0, inventoryPolicy === 'continue' ? 100 : Math.min(100, inventoryQuantity)];
                addCarts.push({
                    discountPrice: price,
                    originalPrice: compareAtPrice,
                    inventory: max,
                    productId: productId,
                    variantId: variantId,
                    img: variantImg || img || '',
                    title: `${title}${t(`secF_${card.id}_title`) ? `\n(${t(`secF_${card.id}_title`)})` : ''}`,
                    quantity,
                    parentVariantId,
                });
            }
            callBuy(addCarts, isLink);
        }
    };

    useEffect(() => {
        const resetGap = () => {
            const winW = window.innerWidth;
            if (winW >= 1440) {
                return setGap(24);
            } else if (winW > 1024) {
                return setGap(20);
            } else {
                return setGap(16);
            }
        };
        resetGap();
    }, []);

    useEffect(() => {
        const initialSolarPanel = async () => {
            const promises: Promise<SolarItemType>[] = [];
            solar_panels?.forEach((item: SolarItemType) => promises.push(getAsynItem(item, locale) as Promise<SolarItemType>));
            const inited = await Promise.all(promises);
            setSolarPanels(inited);
        };
        initialSolarPanel();
    }, []);

    useEffect(() => {
        const initialProductCards = async () => {
            const promises: Promise<ProductItemType>[] = [];
            products?.forEach((item: ProductItemType) => promises.push(getAsynItem(item, locale) as Promise<ProductItemType>));
            const inited = await Promise.all(promises);
            inited?.map((item: ProductItemType) => {
                item.quantity = 1;
                if (item?.childrenIds?.length > 0) {
                    item.children = [] as SolarItemType[];
                    item.childrenIds?.forEach((childId: string, k: number) => {
                        const child: SolarItemType | undefined = solarPanels?.find((sp: SolarItemType) => sp.id === childId);
                        if (child) {
                            const isAC = ['d3p', 'd2', 'd2m'].includes(child.id);
                            const recommand = isAC ? [1] : RecommandMap?.[item.id]?.[child.id] || 0;
                            item.children?.push({
                                ...child,
                                parentVariantId: item.variantId,
                                quantity: Math.max(Math.min(isAC ? 1 : recommand[0], child.inventoryPolicy === 'continue' ? 100 : child.inventoryQuantity), 0),
                                recommand,
                                isActive: k === 0,
                            });
                        }
                    });
                }
            });
            setProductCards(inited);
        };
        solarPanels?.length > 0 && initialProductCards();
    }, [solarPanels]);

    useEffect(() => {
        const { variantId = '', parentVariantId = '', quantity = -1 } = updateSolar || {};
        const targetSP = solarPanels?.find((sp: any) => sp?.variantId === variantId);
        if (variantId && parentVariantId && quantity >= 0 && targetSP) {
            const tempArr = cloneDeep(productCards);
            const cardTargetIdx = tempArr?.findIndex((item: any) => item?.variantId === parentVariantId);
            if (cardTargetIdx !== -1) {
                const solarTargetIdx = tempArr[cardTargetIdx]?.children.findIndex((item: any) => item?.variantId === variantId);
                if (solarTargetIdx !== -1) {
                    tempArr[cardTargetIdx].children[solarTargetIdx]['quantity'] = quantity;
                }
            }
            setProductCards(tempArr);
        }
    }, [updateSolar, solarPanels]);

    return (
        <div id="outdoor-recreation-F" className={`outdoor-recreation-F outdoor-recreation-box inner ${css['outdoor-recreation-F']}`}>
            <h3 className="title">{t('secF_title')}</h3>
            <Swiper
                className="cards swiper-box"
                spaceBetween={gap}
                slidesPerView={'auto'}
                initialSlide={0}
                modules={[Scrollbar, Navigation]}
                navigation
                scrollbar={{
                    hide: false,
                    draggable: true,
                    dragSize: 32,
                    snapOnRelease: true,
                }}
            >
                {productCards?.map((item: ProductItemType, k: number) => {
                    const activeChild = item.children?.find((child: SolarItemType) => child.isActive) || item.children?.[0];
                    const { variantId, id, productTitle, skuTitle, descArr, icons, storage, children, link_more } = item;
                    const { quantity, recommand, energy, inventoryPolicy, inventoryQuantity } = activeChild;
                    const isSP = item.id !== 'ac800';
                    const ac_storage = activeChild?.storage || 0;
                    const recommandStatu = quantity === 0 || !isSP ? 0 : recommand.includes(quantity) ? 1 : 2;
                    const [min, max] = [0, inventoryPolicy === 'continue' ? 100 : Math.min(100, inventoryQuantity)];
                    const isSoldOut = item.inventoryPolicy !== 'continue' && item.inventoryQuantity <= 0;
                    return (
                        <SwiperSlide className="card" key={`${variantId}-${id}`}>
                            <div className="card-info">
                                <div className="card-img">
                                    <EcoImg
                                        pc={
                                            BungleImgMap?.[id]?.[activeChild.id]?.[suffix]?.trim() ||
                                            BungleImgMap?.[id]?.[activeChild.id]?.['en']?.trim() ||
                                            BungleImgMap?.[id]?.[activeChild.id]?.['eu']?.trim() ||
                                            item.variantImg ||
                                            item.img ||
                                            ''
                                        }
                                    />
                                </div>
                                {t(`secF_${id}_power`) && <p className="card-power">{t(`secF_${id}_power`)}</p>}
                                <p className="card-title">{t(`secF_${id}_title`) || productTitle || skuTitle || ''}</p>
                                {descArr?.length > 0 && (
                                    <ul className="card-descs">
                                        {descArr?.map((_: string, m: number) => (
                                            <li className="card-desc" key={`${k}-${m}`}>
                                                {t(`secF_${id}_desc${m + 1}`)}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {icons?.length > 0 && (
                                    <>
                                        {open ? (
                                            <ul className="card-icons">
                                                {icons?.map((icon: string, m: number) => (
                                                    <li className="card-icon" key={`${k}-${m}`}>
                                                        <div className="icon-svg">
                                                            <GetAppIcon appliance={icon} />
                                                        </div>
                                                        <p className="icon-title">
                                                            {t.rich(`secF_${id}_icon${m + 1}_title`, {
                                                                s: (text: string) => text,
                                                            })}
                                                        </p>
                                                        <p className="icon-desc">{t(`secF_${id}_icon${m + 1}_desc`)}</p>
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <span className="card-open" onClick={() => setOpen(true)}>
                                                <svg xmlns="http://www.w3.org/2000/svg" width="9" height="5" viewBox="0 0 9 5" fill="none">
                                                    <path
                                                        d="M1.5 1L4.5 4L7.5 1"
                                                        stroke="#626262"
                                                        strokeWidth="1.21739"
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                    />
                                                </svg>
                                            </span>
                                        )}
                                    </>
                                )}
                                <div className="card-line"></div>
                                <p className="calc-title">
                                    {isSP
                                        ? t('common_card_calculator')
                                        : t.rich('secF_ac800_question', {
                                              name: t(`secF_card_${activeChild.id}`),
                                          })}
                                </p>
                                <p
                                    className={`calc-result ${
                                        recommandStatu === 0 ? 'recommand-0' : recommandStatu === 1 ? 'recommand-range' : 'not-recommand'
                                    }`}
                                >
                                    {isSP
                                        ? recommandStatu === 0
                                            ? locale === 'kr'
                                                ? t.rich('secF_card_0', {
                                                      storagePower: 298,
                                                      s: (text: string) => <span className="s">{text}</span>,
                                                  })
                                                : t.rich('common_card_0', {
                                                      storagePower: formatNumber(storage, locale),
                                                      s: (text: string) => <span className="s">{text}</span>,
                                                  })
                                            : recommandStatu === 1
                                            ? locale === 'kr'
                                                ? t.rich('secF_card_calculator_res', {
                                                      generatePower: formatNumber(calcEnergyGeneration(energy, quantity, locale), locale),
                                                      storagePower: 298,
                                                      s: (text: string) => <span className="s">{text}</span>,
                                                  })
                                                : t.rich('common_card_calculator_res', {
                                                      generatePower: formatNumber(calcEnergyGeneration(energy, quantity, locale), locale),
                                                      storagePower: formatNumber(storage, locale),
                                                      s: (text: string) => <span className="s">{text}</span>,
                                                  })
                                            : t('common_card_no_recommand')
                                        : t.rich('secF_ac800_content', {
                                              storage: formatNumber(ac_storage, locale),
                                              day: Math.round(ac_storage),
                                              s: (text: string) => <span className="s">{text}</span>,
                                          })}
                                </p>
                                <p className="sp-label">{t('common_card_sp_output')}</p>
                                <ul className={`card-children${isSP ? '' : ' AC'}`}>
                                    {children?.map((child: SolarItemType, m: number) => (
                                        <li
                                            className={`card-child${child.isActive ? ' active' : ''}`}
                                            key={`${variantId}-${id}-${child.variantId}-${m}`}
                                            onClick={() => handleChangeChild(item, child)}
                                        >
                                            {t(`secF_card_${child.id}`) || child.productTitle || child.skuTitle || child.id.toUpperCase() || ''}
                                        </li>
                                    ))}
                                </ul>
                                {isSP && (
                                    <>
                                        <p className="sp-label sp-quantity">{t('common_card_sp_quantity')}</p>
                                        <div className="card-children-quantity">
                                            <button
                                                className={`calc-operator${quantity <= min ? ' disabled' : ''}`}
                                                onClick={() => handleQuantityChange(item, -1)}
                                            >
                                                -
                                            </button>
                                            <span className="calc-operator calc-quantity">{quantity}</span>
                                            <button
                                                className={`calc-operator${quantity >= max ? ' disabled' : ''}`}
                                                onClick={() => handleQuantityChange(item, 1)}
                                            >
                                                +
                                            </button>
                                        </div>
                                    </>
                                )}
                            </div>
                            <div className="card-btns">
                                {locale === 'kr' ? (
                                    <div className="card-btn-cart">
                                        <button className={`kr-shop${isSoldOut ? ' disabled' : ''}`} onClick={() => handleBuy(item, true)}>
                                            {t('common_card_buy_new')}
                                        </button>
                                        <button className={`kr-add-cart${isSoldOut ? ' disabled' : ''}`} onClick={() => handleBuy(item)}>
                                            <svg width="24" height="25" viewBox="0 0 24 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path
                                                    fill-rule="evenodd"
                                                    clip-rule="evenodd"
                                                    d="M16.1665 20C16.1665 21.3807 17.2858 22.5 18.6665 22.5C20.0472 22.5 21.1664 21.3807 21.1664 20C21.1664 18.6193 20.0472 17.5 18.6665 17.5C17.2858 17.5 16.1665 18.6193 16.1665 20ZM19.7056 20.017C19.7056 20.5692 19.2579 21.017 18.7057 21.017C18.1534 21.017 17.7057 20.5692 17.7057 20.017C17.7057 19.4647 18.1534 19.017 18.7057 19.017C19.2579 19.017 19.7056 19.4647 19.7056 20.017Z"
                                                    fill="currentColor"
                                                ></path>
                                                <path
                                                    fill-rule="evenodd"
                                                    clip-rule="evenodd"
                                                    d="M6.1665 20C6.1665 21.3807 7.28578 22.5 8.66647 22.5C10.0472 22.5 11.1664 21.3807 11.1664 20C11.1664 18.6193 10.0472 17.5 8.66647 17.5C7.28578 17.5 6.1665 18.6193 6.1665 20ZM9.70565 20.017C9.70565 20.5692 9.25794 21.017 8.70566 21.017C8.15339 21.017 7.70568 20.5692 7.70568 20.017C7.70568 19.4647 8.15339 19.017 8.70566 19.017C9.25794 19.017 9.70565 19.4647 9.70565 20.017Z"
                                                    fill="currentColor"
                                                ></path>
                                                <path
                                                    fill-rule="evenodd"
                                                    clip-rule="evenodd"
                                                    d="M21.1664 6.66667H7.44731L7.18443 4.67432C7.02155 3.432 5.96318 2.5019 4.70956 2.5H2.83332C2.37309 2.5 2 2.8731 2 3.33333C2 3.79357 2.37309 4.16667 2.83332 4.16667H4.70829C5.12491 4.1673 5.4777 4.47733 5.53202 4.89167L6.79876 14.4923C6.96283 15.7441 8.0355 16.6774 9.29863 16.6666L18.0998 16.6667C19.2946 16.6897 20.3479 15.8418 20.5838 14.654L21.9835 7.66362C22.0868 7.1479 21.6923 6.66667 21.1664 6.66667ZM8.45117 14.275L7.66721 8.33333H20.1497L18.9493 14.328C18.8704 14.7252 18.5193 15.0079 18.1157 15.0002L9.29156 15C8.86345 15.0036 8.5059 14.6925 8.45117 14.275Z"
                                                    fill="currentColor"
                                                ></path>
                                                <path
                                                    d="M14.5 9.5C14.5 9.22386 14.2761 9 14 9C13.7239 9 13.5 9.22386 13.5 9.5V11H12C11.7239 11 11.5 11.2239 11.5 11.5C11.5 11.7761 11.7239 12 12 12H13.5V13.5C13.5 13.7761 13.7239 14 14 14C14.2761 14 14.5 13.7761 14.5 13.5V12H16C16.2761 12 16.5 11.7761 16.5 11.5C16.5 11.2239 16.2761 11 16 11H14.5V9.5Z"
                                                    fill="currentColor"
                                                    stroke="currentColor"
                                                    stroke-width="0.3"
                                                ></path>
                                            </svg>
                                        </button>
                                    </div>
                                ) : (
                                    <button className={`card-btn btn-cart${isSoldOut ? ' disabled' : ''}`} onClick={() => handleBuy(item)}>
                                        {isSoldOut ? t('common_card_out') : t('common_card_buy')}
                                    </button>
                                )}
                                <EcoLink href={link_more} className="card-btn btn-more">
                                    {t('common_card_more')}
                                </EcoLink>
                            </div>
                        </SwiperSlide>
                    );
                })}
            </Swiper>
        </div>
    );
}
